<template>
  <div class="lesson-text-box">
    <VLessonPlaceholder v-if="isEmptyBlock" />
    
    <div v-else
      class="w-full h-full m-0 box-border transition-all relative"
      :style="[
        CssStyleHelpers.background(
          fields.mainGroup?.backgroundColor,
          fields.mainGroup?.isShowColor
        ),
        CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
        CssStyleHelpers.margins(fields.mainGroup?.margins),
      ]"
    >
      <div
        class="w-full h-full flex"
        :style="[CssStyleHelpers.flexDirection(fields.mediaGroup?.gridReverse)]"
      >
        <!-- Медиа контент -->
        <div
          v-if="fields.mainGroup?.isShowMedia"
          class="media-background w-full overflow-hidden"
          :style="[
            CssStyleHelpers.filterShadow(fields.mainGroup?.shadow),
            CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
          ]"
        >
          <MediaCard
            fill
            :media-type="fields.mainGroup?.type"
            :image-props="fields.mainGroup?.imageProps"
            :video-props="fields.mainGroup?.videoProps"
            :mask-props="fields.mainGroup?.maskProps"
            :object-fit="fields.mainGroup?.objectFit || 'cover'"
          />
        </div>

        <!-- <pre class="text-black">{{ fields.tableGroup.rows }}</pre> -->

        <!-- Таблиця -->
        <div
          v-if="fields.tableGroup?.rows?.length > 0"
          class="w-full h-full overflow-hidden"
        >
          <div
            :class="[
              'table-container',
              'border',
              'border-gray-300',
              !(
                fields.tableGroup?.gridBorder?.enabled &&
                fields.tableGroup?.gridBorder?.width > 0
              ) ? 'divide-y divide-gray-300' : ''
            ]"
            :style="[
            CssStyleHelpers.borderRadius(fields.tableGroup?.tableBorderRadius),
            CssStyleHelpers.border(fields.tableGroup?.tableBorder, fields.tableGroup?.tableBorder?.enabled),
          ]">
           <div v-for="(row, rowIndex) in fields.tableGroup?.rows" class="grid grid-cols-12 gap-0 w-full " :style="[{ minHeight: '25px' }]">
            <div v-for="(cell, cellIndex) in row.cells" class="col-span-6 h-[100%]" :style="[
              CssStyleHelpers.paddings(fields.tableGroup?.cardInnerSpacing),
              CssStyleHelpers.background(cell?.colorField?.data?.value, cell?.colorField?.data?.isShown),
              getCellBorderStyle(rowIndex, cellIndex),
            ]">
              <TextCard :text="cell?.textField?.data?.text"></TextCard>
            </div>
           </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- NOTE: This file is based on VLessonLayoutBlock3CardView.vue -->
</template>

<script setup lang="ts">
import { computed, PropType } from "vue";
import { useDataFields } from "../composables/data";
import MediaCard from "../cards/MediaCard.vue";
import DialogCard from "../cards/DialogCard.vue";
import TextCard from "../cards/TextCard.vue";
import VLessonPlaceholder from "../VLessonPlaceholder.vue";
import { ILayoutComponentData } from "../types/ILayoutFieldData";
import { CssStyleHelpers } from "../utils/cssStyleHelpers";
const props = defineProps({
  modelValue: {
    type: Object as PropType<ILayoutComponentData | undefined>,
    required: true,
  },
  mode: {
    type: String as PropType<"view" | "edit">,
    default: "view",
  },
});

// Використовуємо дані напряму з newData
const applyDefaultMainMargins = (margins?: Record<string, number>) => ({
  top: margins?.top ?? 50,
  right: margins?.right ?? 0,
  bottom: margins?.bottom ?? 0,
  left: margins?.left ?? 0,
  ...margins,
});

const fields = computed(() => {
  const newData = props.modelValue?.data;
  if (!newData) return { mainGroup: {}, tableGroup: {} };

  const { getData } = useDataFields(newData);
  
  // Отримуємо дані таблиці та створюємо новий масив для забезпечення реактивності
  const tableData = getData('tableGroup.table');
  // Створюємо новий масив, щоб Vue відстежував зміни
  const rows = tableData?.rows && Array.isArray(tableData.rows) 
    ? tableData.rows.map((row: any) => ({ ...row })) 
    : [];

  return {
    mainGroup: {
      isShowMedia: getData('mainGroup.isShowMedia')?.value,
      media: getData('mainGroup.media'),
      mediaMask: getData('mainGroup.mediaMask'),
      isShowColor: getData('mainGroup.isShowColor')?.value,
      backgroundColor: getData('mainGroup.backgroundColor')?.value,
      borderRadius: getData('mainGroup.borderRadius')?.value,
      shadow: getData('mainGroup.shadow'),
      type: getData('mainGroup.media')?.type,
      objectFit: getData('mainGroup.media')?.objectFit ?? 'contain',
      imageProps: {
        imageSrc: getData('mainGroup.media')?.url || getData('mainGroup.media')?.previewUrl || null,
        aspectRatio: getData('mainGroup.media')?.aspectRatio
      },
      videoProps: {
        videoSrc: getData('mainGroup.media')?.url || getData('mainGroup.media')?.previewUrl || null,
        autoplay: getData('mainGroup.media')?.autoplay,
        muted: getData('mainGroup.media')?.muted,
        loop: getData('mainGroup.media')?.loop,
        aspectRatio: getData('mainGroup.media')?.aspectRatio
      },
      maskProps: getData('mainGroup.mask'),
      margins: applyDefaultMainMargins(getData('mainGroup.margins')),
    },
    // Дані та стилі для таблиці
    tableGroup: {
      // Основні дані таблиці - створюємо новий масив для реактивності
      rows: rows,

      // Стилі контейнера таблиці
      cardInnerSpacing: getData('tableGroup.cardInnerSpacing'),
      tableBorderRadius: getData('tableGroup.tableBorderRadius')?.value,
      tableBorder: getData('tableGroup.tableBorder'),
      gridBorder: getData('tableGroup.gridBorder'),
    },
  };
});

const isEditMode = computed(() => props.mode === "edit");

// Перевіряємо чи блок пустий
const isEmptyBlock = computed(() => {
  const hasRows = fields.value?.tableGroup?.rows && fields.value.tableGroup.rows.length > 0;
  const hasMedia = fields.value?.mainGroup?.isShowMedia && (
    fields.value?.mainGroup?.imageProps?.imageSrc || 
    fields.value?.mainGroup?.videoProps?.videoSrc
  );
  const hasColor = fields.value?.mainGroup?.isShowColor && fields.value?.mainGroup?.backgroundColor;
  
  return !(hasRows || hasMedia || hasColor);
});

// Обчислюємо межі комірок лише справа/знизу, щоб не було подвоєнь
const getCellBorderStyle = (rowIndex: number, cellIndex: number) => {
  const gridBorder = (fields.value as any)?.tableGroup?.gridBorder;
  const rows = (fields.value as any)?.tableGroup?.rows || [];

  if (!gridBorder?.enabled || !gridBorder?.width || gridBorder.width <= 0) {
    return { border: 'none' };
  }

  const isLastRow = rowIndex === (rows.length - 1);
  // В цій розмітці 12 колонок і кожна комірка має col-span-6 => 2 колонки
  const isLastCol = (cellIndex % 2) === 1;

  const style = gridBorder?.style || 'solid';
  const color = gridBorder?.color || '#000000';
  const borderValue = `${gridBorder.width}px ${style} ${color}`;

  return {
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: isLastCol ? 'none' : borderValue,
    borderBottom: isLastRow ? 'none' : borderValue,
  } as Record<string, string>;
};
</script>
<style scoped>
@reference "tailwindcss";
.lesson-text-box {
  @apply w-full flex flex-col gap-0;
  container-type: inline-size;
}
.media-background {
  @apply w-full h-full absolute inset-0 z-0;
  border-radius: inherit;
  overflow: hidden;
}
.media-background :deep(.media-card) {
  height: 100%;
}

/* Override @container height:auto !important from MediaContainerLayer and MaskLayer —
   those resets are correct for inline media but must not apply to the background fill. */
.media-background :deep(.media-container-layer),
.media-background :deep(.media-container-layer > div) {
  height: 100% !important;
  min-height: unset !important;
  max-height: unset !important;
  aspect-ratio: unset !important;
}
.media-background :deep(img),
.media-background :deep(video) {
  @apply w-full h-full object-cover;
}
.card-item {
  @apply text-base leading-relaxed relative z-10;
}

.table-container {
  @apply w-full overflow-auto;
}

.table-container table {
  @apply w-full border-collapse;
}

.table-container th,
.table-container td {
  @apply border border-gray-300 px-4 py-2 text-gray-800;
}

.table-container th {
  @apply font-semibold text-left text-gray-800;
}

.table-container tbody tr:hover {
  @apply bg-gray-50;
}


</style>
<style>
@import "../styles/dialogs-styles.css";
</style>
